import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/app-shell";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Search, Loader2, Calendar, User, Activity } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { Helmet } from 'react-helmet';

export default function AuditLogsPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [actionFilter, setActionFilter] = useState("all");

  // Create the query URL with filters
  const queryUrl = `/api/audit-logs?page=${page}&limit=${limit}${
    actionFilter !== "all" ? `&action=${actionFilter}` : ""
  }`;

  // Fetch audit logs
  const { data, isLoading } = useQuery<any>({
    queryKey: [queryUrl],
  });

  const logs = data?.logs || [];
  const totalLogs = data?.total || 0;
  const totalPages = Math.ceil(totalLogs / limit);

  // Filtered logs based on search query
  const filteredLogs = logs.filter((log: any) => {
    if (searchQuery === "") return true;
    return (
      log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (log.details && log.details.toLowerCase().includes(searchQuery.toLowerCase())) ||
      log.ipAddress.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Action type options
  const actionTypes = [
    { value: "all", label: t("auditLogs.allActions") },
    { value: "CREATE_ANALYSIS", label: t("auditLogs.actions.createAnalysis") },
    { value: "UPDATE_ANALYSIS", label: t("auditLogs.actions.updateAnalysis") },
    { value: "DELETE_ANALYSIS", label: t("auditLogs.actions.deleteAnalysis") },
    { value: "SUBMIT_ANALYSIS", label: t("auditLogs.actions.submitAnalysis") },
    { value: "UPLOAD_DATASHEET", label: t("auditLogs.actions.uploadDatasheet") },
    { value: "EXPORT_ANALYSIS", label: t("auditLogs.actions.exportAnalysis") },
    { value: "SHARE_ANALYSIS", label: t("auditLogs.actions.shareAnalysis") },
  ];

  return (
    <AppShell>
      <Helmet>
        <title>{t("auditLogs.pageTitle")} | Pump ECM</title>
        <meta name="description" content={t("auditLogs.pageDescription")} />
      </Helmet>

      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{t("auditLogs.title")}</h1>
        <p className="text-muted-foreground">{t("auditLogs.subtitle")}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("auditLogs.systemLogs")}</CardTitle>
          <CardDescription>{t("auditLogs.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
            <div className="relative w-full md:w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("auditLogs.search")}
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder={t("auditLogs.filterByAction")} />
                </SelectTrigger>
                <SelectContent>
                  {actionTypes.map((action) => (
                    <SelectItem key={action.value} value={action.value}>
                      {action.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select 
                value={String(limit)} 
                onValueChange={(value) => {
                  setLimit(Number(value));
                  setPage(1); // Reset to first page when changing limit
                }}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredLogs.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[150px]">{t("auditLogs.timestamp")}</TableHead>
                    <TableHead className="w-[120px]">{t("auditLogs.user")}</TableHead>
                    <TableHead className="w-[120px]">{t("auditLogs.action")}</TableHead>
                    <TableHead>{t("auditLogs.details")}</TableHead>
                    <TableHead className="w-[120px]">{t("auditLogs.ipAddress")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.map((log: any) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                          {formatDate(new Date(log.timestamp))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-2 text-muted-foreground" />
                          {log.userName || `User ${log.userId}`}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Activity className="h-4 w-4 mr-2 text-primary" />
                          {t(`auditLogs.actions.${log.action.toLowerCase()}`) || log.action}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-md truncate">{log.details}</TableCell>
                      <TableCell>{log.ipAddress}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-10 border rounded-lg bg-muted/30">
              <p className="text-muted-foreground mb-4">
                {searchQuery || actionFilter !== "all" 
                  ? t("auditLogs.noResults") 
                  : t("auditLogs.noLogs")}
              </p>
              {(searchQuery || actionFilter !== "all") && (
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSearchQuery("");
                    setActionFilter("all");
                  }}
                >
                  {t("auditLogs.clearFilters")}
                </Button>
              )}
            </div>
          )}

          {/* Pagination */}
          {!isLoading && filteredLogs.length > 0 && totalPages > 1 && (
            <div className="mt-6 flex justify-end">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setPage(Math.max(page - 1, 1))}
                      className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  
                  {/* First page */}
                  {page > 2 && (
                    <PaginationItem>
                      <PaginationLink onClick={() => setPage(1)}>1</PaginationLink>
                    </PaginationItem>
                  )}
                  
                  {/* Ellipsis if needed */}
                  {page > 3 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}
                  
                  {/* Previous page if not on page 1 */}
                  {page > 1 && (
                    <PaginationItem>
                      <PaginationLink onClick={() => setPage(page - 1)}>
                        {page - 1}
                      </PaginationLink>
                    </PaginationItem>
                  )}
                  
                  {/* Current page */}
                  <PaginationItem>
                    <PaginationLink isActive>{page}</PaginationLink>
                  </PaginationItem>
                  
                  {/* Next page if not on last page */}
                  {page < totalPages && (
                    <PaginationItem>
                      <PaginationLink onClick={() => setPage(page + 1)}>
                        {page + 1}
                      </PaginationLink>
                    </PaginationItem>
                  )}
                  
                  {/* Ellipsis if needed */}
                  {page < totalPages - 2 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}
                  
                  {/* Last page */}
                  {page < totalPages - 1 && (
                    <PaginationItem>
                      <PaginationLink onClick={() => setPage(totalPages)}>
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  )}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setPage(Math.min(page + 1, totalPages))}
                      className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
